import { makeAutoObservable, computed, runInAction } from 'mobx';
import type { Note } from './types';
import type { RootStore } from './RootStore';

export class NoteStore {
  notes = new Map<string, Note>();
  private root: RootStore;

  // Secondary index: `${entityType}:${entityId}` → Set of note IDs (plain, not observable)
  private _entityIndex = new Map<string, Set<string>>();
  // Secondary index: entityId → Set of note IDs (for cross-type lookups)
  private _entityIdIndex = new Map<string, Set<string>>();

  constructor(root: RootStore) {
    this.root = root;
    makeAutoObservable(this, {
      all: computed,
    });
  }

  // ── Index helpers ──────────────────────────────────────────────────────────

  private _entityKey(entityType: string, entityId: string): string {
    return `${entityType}:${entityId}`;
  }

  private _addToIndex(note: Note): void {
    const key = this._entityKey(note.entityType, note.entityId);
    if (!this._entityIndex.has(key)) this._entityIndex.set(key, new Set());
    this._entityIndex.get(key)!.add(note.id);
    if (!this._entityIdIndex.has(note.entityId)) this._entityIdIndex.set(note.entityId, new Set());
    this._entityIdIndex.get(note.entityId)!.add(note.id);
  }

  private _removeFromIndex(note: Note): void {
    const key = this._entityKey(note.entityType, note.entityId);
    this._entityIndex.get(key)?.delete(note.id);
    this._entityIdIndex.get(note.entityId)?.delete(note.id);
  }

  private _set(id: string, note: Note): void {
    const existing = this.notes.get(id);
    if (existing) {
      const oldKey = this._entityKey(existing.entityType, existing.entityId);
      const newKey = this._entityKey(note.entityType, note.entityId);
      if (oldKey !== newKey) {
        this._removeFromIndex(existing);
        this._addToIndex(note);
      }
    } else {
      this._addToIndex(note);
    }
    this.notes.set(id, note);
  }

  private _delete(id: string): void {
    const existing = this.notes.get(id);
    if (existing) this._removeFromIndex(existing);
    this.notes.delete(id);
  }

  // ── Computed ───────────────────────────────────────────────────────────────

  get all(): Note[] {
    const result: Note[] = [];
    for (const n of this.notes.values()) {
      if (!n.isDeleted) result.push(n);
    }
    return result;
  }

  // ── Queries ────────────────────────────────────────────────────────────────

  getByEntity(entityType: string, entityId: string): Note[] {
    const key = this._entityKey(entityType, entityId);
    const ids = this._entityIndex.get(key);
    if (!ids || ids.size === 0) return [];
    const result: Note[] = [];
    for (const id of ids) {
      const n = this.notes.get(id);
      if (n && !n.isDeleted) result.push(n);
    }
    return result;
  }

  getByEntityIds(entityIds: Set<string>): Note[] {
    const result: Note[] = [];
    for (const entityId of entityIds) {
      const ids = this._entityIdIndex.get(entityId);
      if (!ids) continue;
      for (const id of ids) {
        const n = this.notes.get(id);
        if (n && !n.isDeleted) result.push(n);
      }
    }
    return result;
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  loadFromRecords(records: Record<string, unknown>[]): void {
    runInAction(() => {
      for (const r of records) {
        this._set(r.id as string, r as unknown as Note);
      }
    });
  }

  // ── Sync ───────────────────────────────────────────────────────────────────

  applyChanges(changes: { id: string; data?: Record<string, unknown>; isDeleted: boolean }[]): void {
    runInAction(() => {
      for (const c of changes) {
        if (c.isDeleted) this._delete(c.id);
        else if (c.data) this._set(c.id, c.data as unknown as Note);
      }
    });
  }

  removeFromMap(id: string): void {
    this._delete(id);
  }

  // ── Mutations ──────────────────────────────────────────────────────────────

  create(data: Partial<Note>): Note {
    const n: Note = {
      id: crypto.randomUUID(), entityType: data.entityType ?? '', entityId: data.entityId ?? '',
      content: data.content ?? '', userId: data.userId ?? undefined, orgId: '', syncVersion: 0, isDeleted: false,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    this._set(n.id, n);
    this.root.syncStore.queueMutation('note', 'create', n.id, n);
    return n;
  }

  update(id: string, patch: Partial<Note>): void {
    const existing = this.notes.get(id);
    if (!existing) return;
    this._set(id, { ...existing, ...patch, updatedAt: new Date().toISOString() });
    this.root.syncStore.queueMutation('note', 'update', id, patch);
  }

  delete(id: string): void {
    const existing = this.notes.get(id);
    if (existing) {
      this._set(id, { ...existing, isDeleted: true, updatedAt: new Date().toISOString() });
    }
    this.root.syncStore.queueMutation('note', 'delete', id);
  }

  clear(): void {
    this.notes.clear();
    this._entityIndex.clear();
    this._entityIdIndex.clear();
  }
}
